'use client';

import { getBlogArticles, getBlogTopReadArticles } from '@/api/blog';
import {
  IActiveNavConfig,
  IBlogArticleListItem,
  IBlogConfig,
  IBlogConfigContent,
} from '@/api/types/blog';
import Breadcrumbs from '@/components/Breadcrumbs';
import Popup from '@/components/Popup';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useStore } from '@/providers/app-store-provider';
import { colors } from '@/theme/zeelool.theme';
import { BLOG_MARK_LIST } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';

import logger from '@/utils/client-logger';
import { isEmpty, scrollToAnchor } from '@/utils/tools';
import { SxProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { WapMoreOutlined } from '@zeelool/icon';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ArticleList from './components/ArticleList';
import BlogBanner from './components/BlogBanner';
import BlogNav from './components/BlogNav';
import TopMenus from './components/TopMenus';
import TopReadArticles from './components/TopReadArticles';
import TypeBanner from './components/TypeBanner';
import { useBlogMainTrack } from './hooks/blogMainTrack';
import { useTranslations } from '@/i18n/use-translations';

interface BlogPageClientProps {
  homeBanner?: IBlogConfig;
  navList?: IBlogConfig[];
  initialActiveNav?: IActiveNavConfig;
  initialTopReadArticles?: IBlogArticleListItem[];
  initialArticles?: IBlogArticleListItem[];
  initialTotal?: number;
}

export default function BlogPageClient({
  homeBanner,
  navList,
  initialActiveNav,
  initialTopReadArticles,
  initialArticles,
  initialTotal,
}: BlogPageClientProps) {
  const t = useTranslations('blog');
  const { deviceType } = useContext(ResponsiveContext);
  const waiMode = useStore((state) => state.waiMode);
  const isWeb = deviceType === EPlatform.WEB;

  const { sdBlogPageMktExp, sdBlogPageMktClick } = useBlogMainTrack();

  // 页面状态管理
  const [activeNav, setActiveNav] = useState<IActiveNavConfig>(initialActiveNav || {});
  const [topReadArticles, setTopReadArticles] = useState<IBlogArticleListItem[]>(
    initialTopReadArticles || [],
  );
  const [isNavDrawerOpen, setIsNavDrawerOpen] = useState(false);
  const [articles, setArticles] = useState<IBlogArticleListItem[]>(initialArticles || []);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(initialTotal || 0);
  const [loading, setLoading] = useState(false);

  // TopReadArticles 组件的引用
  const topReadArticlesRef = useRef<{ resetSwiper: () => void } | null>(null);
  const showTypeBanner = useMemo(() => {
    return !isEmpty(activeNav);
  }, [activeNav]);

  const typeBannerInfo = useMemo(() => {
    if (activeNav?.mark === BLOG_MARK_LIST.SHOP_ZEELOOL) {
      return {
        banner_image: activeNav.wap_image,
        name: activeNav.name,
        description: activeNav.description,
        jump_target: activeNav.jump_target,
        mark: activeNav.mark,
      };
    } else {
      return activeNav;
    }
  }, [activeNav]);

  const listExp = (article: IBlogArticleListItem, index: number) => {
    sdBlogPageMktExp(
      {
        mkt_name: '最新更新',
        pits_index: index,
        article_name: article.name,
        author_name: article.blog_author_name,
        pits_jump_type: '4',
        pits_jump_content: `/blog/${article.another_name || 'all-blogs'}/${article.jump_path}`,
      },
      activeNav,
    );
  };

  const listItemClick = (article: IBlogArticleListItem, index: number, clickArea: string) => {
    sdBlogPageMktClick(
      {
        mkt_name: '最新更新',
        pits_index: index,
        article_name: article.name,
        author_name: article.blog_author_name,
        click_area: clickArea,
        pits_jump_type: '4',
        pits_jump_content: `/blog/${article.another_name || 'all-blogs'}/${article.jump_path}`,
      },
      activeNav,
    );
  };

  const onActiveNavChange = (nav: IActiveNavConfig) => {
    const isFirstLevel = [BLOG_MARK_LIST.ALL_BLOGS, BLOG_MARK_LIST.SHOP_ZEELOOL].includes(nav.mark);
    const firstLevelNav = navList?.find((item) => item.mark === nav.mark);
    const _activeNav = isFirstLevel
      ? {
          ...firstLevelNav,
          ...(firstLevelNav.content as IBlogConfigContent),
          child_id: firstLevelNav.mark,
          parent_name: firstLevelNav.content,
        }
      : nav;
    setActiveNav(_activeNav as IActiveNavConfig);
    setPage(1);
    setTotal(0);
    setArticles([]);
    setIsNavDrawerOpen(false);
    scrollToAnchor('#type-banner-anchor', isWeb ? 100 : 44);
    getTopReadArticles(_activeNav as IActiveNavConfig);

    // 重置 TopReadArticles 轮播到第一个
    if (topReadArticlesRef.current) {
      topReadArticlesRef.current.resetSwiper();
    }
  };

  // 检查并处理从其他页面跳转过来的导航状态
  useEffect(() => {
    const storedType = sessionStorage.getItem('zl-blog-type-change');
    if (storedType) {
      const targetNav = navList?.find((nav) => nav.mark === storedType);
      if (targetNav && targetNav.content) {
        onActiveNavChange({
          ...targetNav,
          ...targetNav.content,
          child_id: targetNav.mark,
          parent_name: targetNav.content.name || '',
        });
      }
      // 清除存储的类型，避免刷新页面时重复触发
      sessionStorage.removeItem('zl-blog-type-change');
      setIsNavDrawerOpen(false);
    }
  }, [navList, onActiveNavChange]);

  const getTopReadArticles = useCallback(async (_activeNav: IActiveNavConfig) => {
    if (_activeNav.mark === BLOG_MARK_LIST.SHOP_ZEELOOL) {
      return;
    }
    const params = {
      type_id: _activeNav.child_id,
      scene: 1,
    };
    if (_activeNav.mark === BLOG_MARK_LIST.ALL_BLOGS) {
      delete params.type_id;
    }
    const { code, data } = await getBlogTopReadArticles(params);
    if (code === 200) {
      setTopReadArticles(data);
    }
  }, []);

  // 获取文章列表
  const fetchArticles = useCallback(
    async (pageNum: number, shouldAppend = false) => {
      if (!activeNav) return null;

      const params = {
        page: pageNum,
        limit: 18,
        order: 'up_at',
        type_id: activeNav.child_id,
      };

      if (activeNav.mark === 'all_blogs') {
        delete params.type_id;
      }

      try {
        setLoading(true);
        const { data } = await getBlogArticles(params);

        if (shouldAppend) {
          // 追加数据
          setArticles((prevArticles) => [...prevArticles, ...(data.data || [])]);
        } else {
          // 替换数据
          setArticles(data.data || []);
        }
        setTotal(data.total);
        return data;
      } catch (error) {
        logger.error('获取文章列表失败:', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [activeNav],
  );

  // 处理页面变化
  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      fetchArticles(newPage, false);
    },
    [fetchArticles],
  );

  // 处理加载更多
  const handleLoadMore = useCallback(() => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchArticles(nextPage, true);
  }, [page, fetchArticles]);

  // 监听activeNav变化，重新获取文章数据
  useEffect(() => {
    if (!activeNav || activeNav.mark === BLOG_MARK_LIST.SHOP_ZEELOOL) return;

    fetchArticles(1, false);
  }, [activeNav, fetchArticles]);

  const BreadcrumbsContent = ({ className, sx }: { className?: string; sx?: SxProps<Theme> }) => {
    return (
      <Breadcrumbs
        items={[
          { label: 'Home', href: '/' },
          { label: 'ZEELOOL Blog', href: '/blog' },
        ]}
        className={className}
        sx={sx}
      />
    );
  };
  return (
    <div className="min-h-screen bg-[#f4f4f4]">
      {/* 面包屑导航 */}
      <WapOnly>
        <BreadcrumbsContent
          className="pl-24 pt-10 pb-16"
          sx={{
            '& .MuiBreadcrumbs-li a': {
              display: 'block',
              fontSize: '12px',
              lineHeight: '1.5',
            },
            '& .MuiBreadcrumbs-li p,  & .MuiBreadcrumbs-li a > span, & .MuiBreadcrumbs-separator': {
              color: waiMode ? colors.primaryBlack : colors.primaryText,
              fontSize: '12px',
              lineHeight: '1.5',
            },
          }}
        />
      </WapOnly>
      {/* 顶部banner */}
      <BlogBanner
        banner={homeBanner}
        sdBlogPageMktExp={(params: any) => sdBlogPageMktExp(params, activeNav)}
        sdBlogPageMktClick={(params: any) => sdBlogPageMktClick(params, activeNav)}
      />
      <div className="type-banner-anchor" id="type-banner-anchor" />
      <div className={`${deviceType === EPlatform.WEB ? 'flex w-full min-h-screen relative' : ''}`}>
        <WebOnly>
          <div className="left-nav-wrapper w-[352px]">
            <div className="left-nav w-full h-[calc(100vh-100px)] sticky top-[100px] overflow-y-auto hide-scrollbar">
              <BreadcrumbsContent
                className="pl-48 pt-12 text-12"
                sx={{
                  '& .MuiBreadcrumbs-li p, & .MuiBreadcrumbs-li a span, & .MuiBreadcrumbs-separator':
                    {
                      fontSize: '12px',
                      color: colors.primaryBlack,
                    },
                }}
              />
              <BlogNav
                activeNav={activeNav}
                navList={navList}
                onTypeChange={onActiveNavChange}
                title={t('blogCenter')}
              />
            </div>
          </div>
        </WebOnly>
        <div
          className={`${deviceType === EPlatform.WEB ? 'right-content w-[calc(100vw-352px)] pt-[3.44vw] pr-48 pl-24 box-border' : ''}`}
        >
          {/* 分类banner */}
          {showTypeBanner && (
            <TypeBanner
              activeNav={typeBannerInfo}
              sdBlogPageMktExp={(params: any) => sdBlogPageMktExp(params, activeNav)}
              sdBlogPageMktClick={(params: any) => sdBlogPageMktClick(params, activeNav)}
            />
          )}
          {activeNav.mark === BLOG_MARK_LIST.SHOP_ZEELOOL && (
            <TopMenus
              topMenuData={activeNav}
              sdBlogPageMktExp={(params: any) => sdBlogPageMktExp(params, activeNav)}
              sdBlogPageMktClick={(params: any) => sdBlogPageMktClick(params, activeNav)}
            />
          )}
          {activeNav.mark !== BLOG_MARK_LIST.SHOP_ZEELOOL && topReadArticles.length && (
            <TopReadArticles
              activeNav={activeNav}
              sdBlogPageMktExp={(params: any) => sdBlogPageMktExp(params, activeNav)}
              sdBlogPageMktClick={(params: any) => sdBlogPageMktClick(params, activeNav)}
              topReadArticles={topReadArticles}
              onRef={(ref) => {
                topReadArticlesRef.current = ref;
              }}
            />
          )}
          {activeNav.mark !== BLOG_MARK_LIST.SHOP_ZEELOOL && (
            <ArticleList
              title="Latest News"
              articles={articles}
              page={page}
              total={total}
              loading={loading}
              listExp={listExp}
              listItemClick={listItemClick}
              onPageChange={handlePageChange}
              onLoadMore={handleLoadMore}
            />
          )}
          {/* 底部占位 */}
          <div className="h-[40px]" />
        </div>
      </div>
      {/* 浮动导航按钮 */}
      <WapOnly>
        <div
          className="float-nav fixed z-10 bottom-[100px] left-0  h-32 w-40 bg-white rounded-r-[50px] flex items-center justify-center shadow-[0 10px 6px 0 rgba(40, 40, 40, 0.1)] border-[1px] border-solid border-#D4D4D4"
          onClick={() => {
            setIsNavDrawerOpen(true);
          }}
        >
          <WapMoreOutlined className="text-24 pr-4" />
        </div>
        <Popup
          open={isNavDrawerOpen}
          position="left"
          width="72%"
          round={false}
          bodyClassName="!p-0"
          onClose={() => setIsNavDrawerOpen(false)}
        >
          <BlogNav
            activeNav={activeNav}
            navList={navList}
            onTypeChange={onActiveNavChange}
            onClose={() => setIsNavDrawerOpen(false)}
          />
        </Popup>
      </WapOnly>
    </div>
  );
}
